/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import Select from "react-select";

import FormLabel from "./FormLabel";
import styled from "styled-components";
import FormRequiredText from "./FormRequiredText";

export const SelectStyled = styled(Select)`
  div {
    border-radius: 0;
  }
`;

const FormSelect = (props) => {
    const {
        label,
        options,
        onChange,
        value,
        disabled,
        isClearable,
        placeholder,
        required,
        isInvalid,
        isMulti,
        size
    } = props;

    const customStyles = {
        control: (styles) => ({ ...styles, borderColor: '#c13727 !important' }),
    }

    // Create small size styles to match Bootstrap FormControl sm
    const smallStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: 'calc(1.5em + 0.5rem + 2px)', // matches Bootstrap input-height-sm
            // Don't set fixed height - allow control to grow when pills are present
            fontSize: '0.875rem', // matches Bootstrap font-size-sm
        }),
        valueContainer: (provided) => ({
            ...provided,
            minHeight: 'calc(1.5em + 0.5rem + 2px)', // Allow container to grow
            padding: '0.25rem 0.5rem', // matches Bootstrap input-padding-y-sm and input-padding-x-sm
            alignItems: 'center', // Center pills vertically
        }),
        input: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
            padding: '0',
            margin: '0',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            minHeight: 'calc(1.5em + 0.5rem + 2px)', // Allow indicators to align with growing control
            alignItems: 'center', // Center indicators vertically
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
        }),
        multiValue: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
            backgroundColor: '#e9ecef', // Bootstrap's gray-200 for pill background
            borderRadius: '0.25rem', // Bootstrap's border-radius-sm
            padding: '0.05rem 0.25rem', // Smaller padding for small size
            margin: '0.125rem 0.125rem 0.125rem 0', // Smaller margin for small size (no left margin to align with container)
            minHeight: '1.25rem', // Smaller height for small pills
            display: 'flex',
            alignItems: 'center', // Center pill content vertically
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
            padding: '0.125rem 0.25rem', // Smaller padding for label
            color: '#495057', // Bootstrap's gray-700 for text color
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
            padding: '0.125rem', // Smaller padding for remove button
            borderRadius: '0 0.25rem 0.25rem 0', // Rounded right side only
            ':hover': {
                backgroundColor: '#dee2e6', // Bootstrap's gray-300 on hover
                color: '#495057',
            },
        }),
    }

    // Create large size styles to match Bootstrap FormControl lg
    const largeStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: 'calc(1.5em + 1rem + 2px)', // matches Bootstrap input-height-lg
            // Don't set fixed height - allow control to grow when pills are present
            fontSize: '1.25rem', // matches Bootstrap font-size-lg
        }),
        valueContainer: (provided) => ({
            ...provided,
            minHeight: 'calc(1.5em + 1rem + 2px)', // Allow container to grow
            padding: '0.5rem 1rem', // matches Bootstrap input-padding-y-lg and input-padding-x-lg
            alignItems: 'center', // Center pills vertically
        }),
        input: (provided) => ({
            ...provided,
            fontSize: '1.25rem',
            padding: '0',
            margin: '0',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            minHeight: 'calc(1.5em + 1rem + 2px)', // Allow indicators to align with growing control
            alignItems: 'center', // Center indicators vertically
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: '1.25rem',
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: '1.25rem',
        }),
        multiValue: (provided) => ({
            ...provided,
            fontSize: '1.25rem',
            backgroundColor: '#e9ecef', // Bootstrap's gray-200 for pill background
            borderRadius: '0.25rem', // Bootstrap's border-radius-sm
            padding: '0.25rem 0.5rem', // Larger padding for large size
            margin: '0.25rem 0.25rem 0.25rem 0', // Larger margin for large size (no left margin to align with container)
            minHeight: '1.5rem', // Larger height for large pills
            display: 'flex',
            alignItems: 'center', // Center pill content vertically
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            fontSize: '1.25rem',
            padding: '0.25rem 0.5rem', // Larger padding for label
            color: '#495057', // Bootstrap's gray-700 for text color
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            fontSize: '1.25rem',
            padding: '0.25rem', // Larger padding for remove button
            borderRadius: '0 0.25rem 0.25rem 0', // Rounded right side only
            ':hover': {
                backgroundColor: '#dee2e6', // Bootstrap's gray-300 on hover
                color: '#495057',
            },
        }),
    }

    // Merge custom styles with size-specific styles
    let finalStyles;
    if (size === 'sm') {
        finalStyles = {
            ...smallStyles,
            control: (styles) => ({
                ...smallStyles.control(styles),
                ...(isInvalid ? customStyles.control(styles) : {}),
            })
        };
    } else if (size === 'lg') {
        finalStyles = {
            ...largeStyles,
            control: (styles) => ({
                ...largeStyles.control(styles),
                ...(isInvalid ? customStyles.control(styles) : {}),
            })
        };
    } else {
        finalStyles = isInvalid ? customStyles : undefined;
    }

    return (
        <>
            {label && (
                <FormLabel>
                    {label}
                    {required && (
                        <FormRequiredText> *</FormRequiredText>
                    )}
                </FormLabel>
            )}
            <SelectStyled
                options={options}
                onChange={(v) => onChange(v)}
                value={value}
                isDisabled={disabled}
                isClearable={isClearable}
                placeholder={placeholder}
                styles={finalStyles}
                isMulti={isMulti}
                menuPortalTarget={document.body}
                size={size}
            />
        </>
    );
}

export default FormSelect;
